import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

// ─── Types ───────────────────────────────────────────────────────────────────

export type TypeNote = 'devoir' | 'examen';
export type StatutReclamation = 'ouverte' | 'en_examen' | 'acceptee' | 'rejetee';

export interface HistoriqueEntry {
  date: string;
  modifie_par: string;
  champ: string;
  ancienne_valeur: string;
  nouvelle_valeur: string;
  motif: string;
  commentaire: string;
}

export interface NoteDevoirDetail {
  id: number;
  note1: number | null;
  absent1: boolean;
  note2: number | null;
  absent2: boolean;
  moyenne_devoirs: string | null;
  verrouille: boolean;
  saisi_par: string | null;
  modifie_par: string | null;
  date_saisie: string | null;
  date_modification: string | null;
  annee_academique: string;
  semestre: string;
}

export interface NoteExamenDetail {
  id: number;
  note_examen: number | null;
  absent: boolean;
  est_rattrapage: boolean;
  verrouille: boolean;
  saisi_par: string | null;
  modifie_par: string | null;
  date_saisie: string | null;
  date_modification: string | null;
  annee_academique: string;
  semestre: string;
}

export interface OcrDevoirDetail {
  ligne_id: number;
  numero_ligne: number;
  numero_page: number;
  image_base64: string | null;
  image_ligne_url: string | null;
  releve_code: string;
  releve_matiere: string | null;
  releve_scan_url: string | null;
  releve_date_scan: string | null;
  releve_annee_academique: string;
  note_d1_validee: string | null;
  note_d2_validee: string | null;
  absent_d1: boolean;
  absent_d2: boolean;
}

export interface OcrExamenDetail {
  ligne_id: number;
  numero_ligne: number;
  numero_page: number;
  image_base64: string | null;
  image_ligne_url: string | null;
  releve_code: string;
  releve_matiere: string | null;
  releve_scan_url: string | null;
  releve_date_scan: string | null;
  releve_annee_academique: string;
  note_finale: string | null;
  source_note: string;
  absent_valide: boolean;
}

export interface Reclamation {
  id: number;
  etudiant: number;
  etudiant_matricule: string;
  etudiant_nom: string;
  etudiant_prenoms: string;
  type_note: TypeNote;
  type_note_display: string;
  champ_conteste: string;
  champ_conteste_display: string;
  matiere: number;
  matiere_nom: string;
  matiere_code: string;
  annee_academique: string;
  semestre: string;
  note_originale: string | null;
  note_attendue: string | null;
  motif: string;
  statut: StatutReclamation;
  statut_display: string;
  note_corrigee: string | null;
  decision_commentaire: string;
  note_devoir: number | null;
  note_examen: number | null;
  note_devoir_detail: NoteDevoirDetail | null;
  note_examen_detail: NoteExamenDetail | null;
  ligne_ocr_devoir: number | null;
  ocr_devoir_detail: OcrDevoirDetail | null;
  ligne_ocr_correction: number | null;
  ocr_examen_detail: OcrExamenDetail | null;
  fichier_preuve: string | null;
  fichier_preuve_url: string | null;
  commentaire_preuve: string;
  ouverte_par: number | null;
  ouverte_par_nom: string | null;
  traitee_par: number | null;
  traitee_par_nom: string | null;
  date_ouverture: string;
  date_traitement: string | null;
  historique: HistoriqueEntry[];
}

export interface ReclamationCreate {
  etudiant: number;
  type_note: TypeNote;
  champ_conteste?: string;
  matiere: number;
  annee_academique: string;
  semestre: string;
  note_originale?: number | null;
  note_attendue?: number | null;
  motif: string;
  note_devoir?: number | null;
  note_examen?: number | null;
  ligne_ocr_devoir?: number | null;
  ligne_ocr_correction?: number | null;
  commentaire_preuve?: string;
}

export interface ReclamationFilters {
  statut?: StatutReclamation;
  type_note?: TypeNote;
  annee_academique?: string;
  semestre?: string;
  etudiant_id?: number;
  matiere_id?: number;
}

export interface DecisionPayload {
  decision: 'en_examen' | 'acceptee' | 'rejetee';
  note_corrigee?: number | null;
  decision_commentaire?: string;
}

export interface ReclamationStats {
  total: number;
  ouvertes: number;
  en_examen: number;
  acceptees: number;
  rejetees: number;
}

// ─── API ─────────────────────────────────────────────────────────────────────

export const reclamationsApi = createApi({
  reducerPath: 'reclamationsApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['Reclamation', 'ReclamationStats'],
  endpoints: (builder) => ({
    // Liste des réclamations
    getReclamations: builder.query<Reclamation[], ReclamationFilters>({
      query: (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.statut) params.set('statut', filters.statut);
        if (filters.type_note) params.set('type_note', filters.type_note);
        if (filters.annee_academique) params.set('annee_academique', filters.annee_academique);
        if (filters.semestre) params.set('semestre', filters.semestre);
        if (filters.etudiant_id) params.set('etudiant_id', String(filters.etudiant_id));
        if (filters.matiere_id) params.set('matiere_id', String(filters.matiere_id));
        const qs = params.toString();
        return `/reclamations/${qs ? '?' + qs : ''}`;
      },
      transformResponse: (response: any) => {
        if (response && typeof response === 'object' && 'results' in response) {
          return response.results;
        }
        return Array.isArray(response) ? response : [];
      },
      providesTags: ['Reclamation'],
    }),

    // Détail d'une réclamation
    getReclamation: builder.query<Reclamation, number>({
      query: (id) => `/reclamations/${id}/`,
      providesTags: (_result, _error, id) => [{ type: 'Reclamation', id }],
    }),

    // Créer une réclamation
    createReclamation: builder.mutation<Reclamation, ReclamationCreate>({
      query: (body) => ({ url: '/reclamations/', method: 'POST', body }),
      invalidatesTags: ['Reclamation', 'ReclamationStats'],
    }),

    // Créer une réclamation avec fichier preuve (FormData)
    createReclamationAvecFichier: builder.mutation<Reclamation, FormData>({
      query: (formData) => ({
        url: '/reclamations/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Reclamation', 'ReclamationStats'],
    }),

    // Modifier une réclamation (patch)
    updateReclamation: builder.mutation<Reclamation, { id: number; data: Partial<ReclamationCreate> }>({
      query: ({ id, data }) => ({ url: `/reclamations/${id}/`, method: 'PATCH', body: data }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Reclamation', id }, 'Reclamation'],
    }),

    // Supprimer une réclamation
    deleteReclamation: builder.mutation<void, number>({
      query: (id) => ({ url: `/reclamations/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['Reclamation', 'ReclamationStats'],
    }),

    // Traiter une réclamation (décision)
    traiterReclamation: builder.mutation<Reclamation, { id: number } & DecisionPayload>({
      query: ({ id, ...body }) => ({
        url: `/reclamations/${id}/traiter/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Reclamation', id }, 'Reclamation', 'ReclamationStats'],
    }),

    // Statistiques
    getReclamationStats: builder.query<ReclamationStats, ReclamationFilters | undefined>({
      query: (filters = {}) => {
        const params = new URLSearchParams();
        if (filters?.annee_academique) params.set('annee_academique', filters.annee_academique);
        if (filters?.semestre) params.set('semestre', filters.semestre);
        const qs = params.toString();
        return `/reclamations/statistiques/${qs ? '?' + qs : ''}`;
      },
      providesTags: ['ReclamationStats'],
    }),
  }),
});

export const {
  useGetReclamationsQuery,
  useGetReclamationQuery,
  useCreateReclamationMutation,
  useCreateReclamationAvecFichierMutation,
  useUpdateReclamationMutation,
  useDeleteReclamationMutation,
  useTraiterReclamationMutation,
  useGetReclamationStatsQuery,
} = reclamationsApi;
